/* eslint-disable no-use-before-define */
import React, {PureComponent} from 'react';
import PropTypes from 'prop-types';
import cx from 'classnames';
import lodashIsEqual from 'lodash/isEqual';
import i18n from '../../utils/i18n';
import {
  documentCollectionType,
  idToTitleMapType,
  issueWrapperArrayType,
  locationsTreeNodesType,
  oxygenType,
  oxygenUserType,
  sortPropType
} from '../../constants/proptype-validation';
import A from '@adsk/bim360-matrix-react-a';
import AssigneeCell from '@adsk/bim360-matrix-react-assignee-cell';
import {Column} from '@adsk/bim360-matrix-react-base-table';
import DocumentCell from '@adsk/bim360-matrix-react-document-cell';
import IconCell from '@adsk/bim360-matrix-react-icon-cell';
import InlineLoader from '@adsk/bim360-matrix-react-inline-loader';
import LocationPicker from '@adsk/bim360-matrix-react-location-picker';
import MatrixTable from '@adsk/bim360-matrix-react-matrix-table';
import Tooltip from '@adsk/bim360-matrix-react-tooltip';
import OverflowTooltip from '@adsk/bim360-matrix-react-overflow-tooltip';
import {getDocumentName, isDocumentUnavailable} from '../../utils/document-helpers';
import {findProjectMemberInOxygenMappingById, getCompanyDisplayName} from '../../utils/userHelpers';
import {getUrn, issueLink} from '../../utils/issues-helpers';
import {isLate, toUtcLongDate, toLocalLongDate} from '../../utils/date-helpers';
import {sortAlphabeticallyByTitle} from '../../utils/sortingHelpers';
import {getAttributeValueFromIssue} from '../../utils/issueAttributes-helpers';
import {changeLocalStorageKey, getLocalStorageByKey} from '../../utils/localStorage-helpers';
import ProjectListTableStatusCell from './ProjectListTableStatusCell';
import featureFlags, {FEATURE_FLAGS} from '../../utils/featureFlags';
import flatMap from 'lodash/flatMap';
import {EmptyStateDataDidntLoad, EmptyStateNotFound, EmptyStateSublocationsError} from './IssueEmptyStates';
import {isTooManySublocationsError} from '../../utils/error-helpers';
import {ISSUE_ATTRIBUTE_NAMES} from '../../constants/issue-attribute-names';
import {getChecklistInstance, getDeeplinkUrl} from '../../utils/checklist-helper';
import ChecklistCell from '../common/ChecklistCell';

class ProjectListMatrixTable extends PureComponent {
  constructor(props) {
    super(props);

    this.renderColumn = this.renderColumn.bind(this);
    this.renderEmptyState = this.renderEmptyState.bind(this);
    this.renderCustomAttributeColumn = this.renderCustomAttributeColumn.bind(this);
    this.renderStatus = this.renderStatus.bind(this);
    this.renderLocation = this.renderLocation.bind(this);
    this.renderAssignee = this.renderAssignee.bind(this);
    this.renderDocument = this.renderDocument.bind(this);
    this.rootCauseDataGetter = this.rootCauseDataGetter.bind(this);
    this.issueTypeDataGetter = this.issueTypeDataGetter.bind(this);
    this.projectMemberNameDataGetter = this.projectMemberNameDataGetter.bind(this);
    this.userDisplayNameWithCompanyDataGetter = this.userDisplayNameWithCompanyDataGetter.bind(this);
    this.companyNameDataGetter = this.companyNameDataGetter.bind(this);
    this.textDataGetter = this.textDataGetter.bind(this);
    this.dueDateClassname = this.dueDateClassname.bind(this);
    this.shouldIncludeColumn = this.shouldIncludeColumn.bind(this);
    this.renderUserCell = this.renderUserCell.bind(this);
    this.renderChecklist = this.renderChecklist.bind(this);
    this.state = {
      showFullStatusColumn: this.shouldShowFullStatusColumn(),
      columns: this.getInitialColumnsState(props)
    };

    this.columnGroupKeys = [
      {
        key: 'STANDARD',
        title: i18n.t('issueAttributes.columnGroups.standard')
      },
      {
        key: 'CUSTOM_ATTRIBUTES',
        title: i18n.t('issueAttributes.columnGroups.custom')
      }
    ];

    const analyticsIdSuffix = props.analyticsId ? `__${props.analyticsId}` : '';
    this.storageKey = `issues-ui__issues-table__${props.projectId}${analyticsIdSuffix}`;

    // Remove on https://jira.autodesk.com/browse/BTLV-11032
    if (props.analyticsId) {
      const oldStorageKey = `issues-ui__issues-table__${props.projectId}_columnsState`;
      const newStorageKey = `${this.storageKey}_columnsState`;
      if (!getLocalStorageByKey(newStorageKey) && oldStorageKey !== newStorageKey) {
        changeLocalStorageKey(oldStorageKey, newStorageKey);
      }
    }
    window.addEventListener('resize', this.onResize);
  }

  componentDidUpdate(prevProps) {
    if (!lodashIsEqual(prevProps.customAttributes, this.props.customAttributes)) {
      this.initiatizeColumnsState();
    }
  }

  componentWillUnmount() {
    window.removeEventListener('resize', this.onResize);
  }

  initiatizeColumnsState() {
    this.setState({
      columns: this.getInitialColumnsState(this.props)
    });
  }

  shouldShowFullStatusColumn() {
    return window.innerWidth >= 1920;
  }

  shouldIncludeColumn(columnKey) {
    const columnsDisabledByFF = featureFlags.get(FEATURE_FLAGS.ISSUES_LIST_OPENED_ON_CLOSED_ON_COLUMNS)
      ? []
      : [ISSUE_COLUMN_KEYS.OPENED_AT, ISSUE_COLUMN_KEYS.CLOSED_AT];
    !featureFlags.get(FEATURE_FLAGS.CHECKLIST_INDICATOR) && columnsDisabledByFF.push(ISSUE_COLUMN_KEYS.CHECKLIST);
    return !columnsDisabledByFF.includes(columnKey);
  }

  getInitialColumnsState(props = this.props) {
    const {isMinimalTableColumns, customAttributes} = props;
    return flatMap(ProjectListMatrixTable.ColumnsOrder.filter(this.shouldIncludeColumn), columnKey => {
      const hidden =
        (isMinimalTableColumns && hideInMinimalColumnMode.includes(columnKey)) ||
        ProjectListMatrixTable.HiddenColumns.includes(columnKey);
      const frozen = ProjectListMatrixTable.FrozenColumns[columnKey]
        ? ProjectListMatrixTable.FrozenColumns[columnKey]
        : false;
      if (columnKey === ISSUE_COLUMN_KEYS.CUSTOM_ATTRIBUTES) {
        return customAttributes.sort(sortAlphabeticallyByTitle).map(customAttribute => ({
          columnKey: customAttribute.id,
          hidden: true,
          frozen: false,
          isCustom: true
        }));
      }
      return {columnKey, hidden, frozen};
    });
  }

  getTextCellValue(value) {
    return value ?? '-';
  }

  projectMemberNameDataGetter({rowData, column}) {
    const projectMemberId = rowData[column.dataKey];
    const displayName = this.props.getEntityDisplayNameById(projectMemberId, i18n.t('activity.formerUser'), null);
    return this.getTextCellValue(displayName);
  }

  userDisplayNameWithCompanyDataGetter({rowData, column}) {
    const projectMemberId = rowData[column.dataKey];
    const displayName = this.props.getUserDisplayNameWithCompany(projectMemberId, i18n.t('activity.formerUser'), null);
    return this.getTextCellValue(displayName);
  }

  companyNameDataGetter({rowData, column}) {
    const projectMemberId = rowData[column.dataKey];
    const displayName = getCompanyDisplayName(this.props.oxygen, projectMemberId);
    return this.getTextCellValue(displayName);
  }

  textDataGetter({rowData, column}) {
    const data = rowData[column.dataKey];
    return this.getTextCellValue(data);
  }

  rootCauseDataGetter({rowData, column}) {
    const data = rowData[column.dataKey];
    const rootCause = this.props.rootCauseEntitiesMap[data];
    const rootCauseText = rootCause?.title;
    return this.getTextCellValue(rootCauseText);
  }

  issueTypeDataGetter({rowData, column}) {
    const data = rowData[column.dataKey];
    return this.getTextCellValue(this.props.issueTypesAndSubTypesTitleMap[data]);
  }

  dateDataGetter({column, rowData}) {
    const data = rowData[column.dataKey];
    return data ? toLocalLongDate(data) : '-';
  }

  duedateDataGetter({column, rowData}) {
    const data = rowData[column.dataKey];
    return data ? toUtcLongDate(data) : '-';
  }

  dueDateClassname({column, rowData}) {
    const data = rowData[column.dataKey];
    const isClosed = rowData[ISSUE_ATTRIBUTE_NAMES.CLOSED_AT];
    return cx('ProjectListMatrixTable__due-date-column', {
      'ProjectListMatrixTable__due-date-column--is-late': !isClosed && isLate(data)
    });
  }

  getRowProps({rowData}) {
    return {
      tagName: A,
      href: typeof issueLink.func === 'function' ? issueLink.func(rowData.id) : '',
      target: '_blank'
    };
  }

  handleDocumentClick(event, issue, documentName) {
    event.preventDefault();
    event.stopPropagation();

    const {type, id} = issue;
    this.props.navigateToIssueInDocumentAction(getUrn(issue), documentName, type, id);
  }

  handleChecklistClick(event, checklist, section, projectId) {
    event.preventDefault();
    event.stopPropagation();

    const url = getDeeplinkUrl(checklist, section, projectId);
    if (url) {
      window.open(url);
    }
  }

  onResize = () => {
    this.setState({
      showFullStatusColumn: this.shouldShowFullStatusColumn()
    });
  };

  onRowClick = ({event, rowData}) => {
    // ctrl+click or cmd+click should have default behavior of anchor link
    if (event.ctrlKey || event.metaKey) {
      return;
    }

    event.preventDefault();
    this.props.onRowClick({attributes: rowData});
  };

  onColumnSort = ({column}) => {
    this.props.onSort(column.sortField);
  };

  renderInnerCell({cellData}) {
    return <div className="ProjectListMatrixTable__inner-cell">{cellData}</div>;
  }

  renderStatus({cellData, container}) {
    return (
      <ProjectListTableStatusCell
        container={container}
        cellData={cellData}
        showFull={this.state.showFullStatusColumn}
      />
    );
  }
  renderInlineLoader() {
    return <InlineLoader className="ProjectListMatrixTable__inline-loader" />;
  }

  renderUserCell(cellContext) {
    const {rowData, column} = cellContext;
    const cellData = rowData[column.dataKey];
    return cellData &&
      !this.props.isDeletedUsersFetched &&
      !this.props.getUserDisplayNameWithCompany(cellData, null, null) ? (
        this.renderInlineLoader()
      ) : (
        <OverflowTooltip theme={Tooltip.Themes.DARK} autoTipOnEllipsis>
          {column.dataGetter(cellContext)}
        </OverflowTooltip>
      );
  }

  renderLocation({cellData, container}) {
    if (!cellData) {
      return '-';
    }
    if (this.props.isLocationsTreeLoading) {
      return this.renderInlineLoader();
    }

    return (
      <LocationPicker
        locationApiPayload={this.props.locationsTreeNodes}
        loadingError={this.props.isLocationsLoadingError}
        selectedEventKey={cellData}
        readOnlyEmptyValuePlaceholder=""
        readOnlyValue
        popoverContainer={container}
      />
    );
  }

  renderAssignee({cellData, rowData, container}) {
    const assignedTo = findProjectMemberInOxygenMappingById(rowData.assignedTo, this.props.oxygen) || cellData;
    if (!assignedTo) {
      return '-';
    }

    return <AssigneeCell container={container} primaryAssignee={assignedTo} currentUser={this.props.currentUser} />;
  }

  renderDocument({cellData, rowData, container}) {
    if (!cellData || cellData.length === 0) {
      return '-';
    }

    const [linkedDocument] = cellData;
    const urn = linkedDocument.urn;
    const isLoading = this.props.fetchingIssuesUrns && this.props.fetchingIssuesUrns.includes(urn);
    const documentName = getDocumentName(this.props.documentCollection, urn);

    return (
      <DocumentCell
        documentName={documentName}
        unavailableDocument={!isLoading && isDocumentUnavailable(this.props.documentCollection, urn)}
        loadingDocument={isLoading}
        onDocumentClick={e => this.handleDocumentClick(e, rowData, documentName)}
        container={container}
      />
    );
  }

  renderChecklist({cellData, container}) {
    if (!cellData || cellData.length === 0) {
      return '-';
    }

    const linkedChecklist = cellData.find(item => item.type === 'checklist');
    const instanceUrn = linkedChecklist?.data?.urn3;
    const sectionUrn = linkedChecklist?.data?.urn2;
    const itemUrn = linkedChecklist?.data?.urn1;

    const isLoading =
      this.props.fetchingChecklistInstances && this.props.fetchingChecklistInstances.includes(instanceUrn);

    const checklist = getChecklistInstance(this.props.checklistCollection, instanceUrn, sectionUrn, itemUrn);

    return (
      <ChecklistCell
        checklistName={checklist?.instance?.title}
        onChecklistClick={e =>
          this.handleChecklistClick(e, checklist?.instance, checklist?.section, this.props.projectId)}
        loadingChecklist={isLoading}
        container={container}
      />
    );
  }

  renderColumn(columnKey, hidden, frozen) {
    const basicProps = {
      key: ISSUE_COLUMN_KEYS[columnKey],
      dataKey: ISSUE_COLUMN_DATA_KEYS[columnKey],
      groupKey: 'STANDARD',
      resizable: true,
      minWidth: 60,
      hidden,
      frozen
    };

    const statusColumnClassname = cx('ProjectListMatrixTable__status-column-header', {
      'ProjectListMatrixTable__status-column-header--show-full': this.state.showFullStatusColumn
    });

    const {isDeletedUsersFetched} = this.props;

    switch (columnKey) {
      case ISSUE_COLUMN_KEYS.STATUS:
        return (
          <Column
            {...basicProps}
            hidden={false}
            width={this.state.showFullStatusColumn ? 160 : 30}
            title={i18n.t('issueAttributes.status.label')}
            sortable
            disabled
            resizable={false}
            sortField={ISSUE_SORTABLE_COLUMN_KEYS[columnKey]}
            className="ProjectListMatrixTable__status-column"
            headerClassName={statusColumnClassname}
            cellRenderer={this.renderStatus}
          />
        );
      case ISSUE_COLUMN_KEYS.ID:
        return (
          <Column
            {...basicProps}
            width={70}
            title={i18n.t('issueAttributes.identifier.label')}
            className="ProjectListMatrixTable__display-id-column"
            sortField={ISSUE_SORTABLE_COLUMN_KEYS[columnKey]}
            sortable
            disabled
            hidden={false}
          />
        );
      case ISSUE_COLUMN_KEYS.TITLE:
        return (
          <Column
            {...basicProps}
            width={170}
            title={i18n.t('issueAttributes.title.label')}
            dataGetter={this.textDataGetter}
            className="ProjectListMatrixTable__title-column"
            sortField={ISSUE_SORTABLE_COLUMN_KEYS[columnKey]}
            flexGrow={1}
            sortable
          />
        );
      case ISSUE_COLUMN_KEYS.LOCATION:
        return (
          <Column
            {...basicProps}
            width={150}
            title={i18n.t('issueAttributes.locationDetails.label')}
            dataGetter={this.textDataGetter}
            className="ProjectListMatrixTable__location-column"
            sortField={ISSUE_SORTABLE_COLUMN_KEYS[columnKey]}
            sortable
          />
        );
      case ISSUE_COLUMN_KEYS.LOCATION_ID:
        return (
          <Column
            {...basicProps}
            width={120}
            title={i18n.t('issueAttributes.lbsLocation.label')}
            cellRenderer={this.renderLocation}
            className="ProjectListMatrixTable__location-column"
          />
        );
      case ISSUE_COLUMN_KEYS.ROOT_CAUSE:
        return (
          <Column
            {...basicProps}
            width={120}
            title={i18n.t('issueAttributes.rootCause.label')}
            className="ProjectListMatrixTable__root-cause-column"
            dataGetter={this.rootCauseDataGetter}
            sortField={ISSUE_SORTABLE_COLUMN_KEYS[columnKey]}
            sortable={this.props.isSortByTypeAndRootCauseEnabled}
          />
        );
      case ISSUE_COLUMN_KEYS.CREATED_BY:
        return (
          <Column
            {...basicProps}
            width={120}
            title={i18n.t('issueAttributes.createdBy.label')}
            dataGetter={this.userDisplayNameWithCompanyDataGetter}
            className="ProjectListMatrixTable__created-by-column"
            cellRenderer={this.renderUserCell}
            data-deleted-users-fetched={isDeletedUsersFetched}
          />
        );
      case ISSUE_COLUMN_KEYS.ASSIGNED_TO:
        return (
          <Column
            {...basicProps}
            width={150}
            title={i18n.t('issueAttributes.assignedTo.label')}
            className="ProjectListMatrixTable__assigned-to-column"
            dataGetter={this.projectMemberNameDataGetter}
            cellRenderer={this.renderUserCell}
            data-deleted-users-fetched={isDeletedUsersFetched}
          />
        );
      case ISSUE_COLUMN_KEYS.COMPANY:
        return (
          <Column
            {...basicProps}
            width={120}
            title={i18n.t('issueAttributes.company.label')}
            className="ProjectListMatrixTable__company-column"
            dataGetter={this.companyNameDataGetter}
          />
        );
      case ISSUE_COLUMN_KEYS.OWNER:
        return (
          <Column
            {...basicProps}
            width={120}
            title={i18n.t('issueAttributes.owner.label')}
            className="ProjectListMatrixTable__owner-column"
            dataGetter={this.projectMemberNameDataGetter}
            cellRenderer={this.renderUserCell}
            data-deleted-users-fetched={isDeletedUsersFetched}
          />
        );
      case ISSUE_COLUMN_KEYS.OPENED_AT:
        return (
          <Column
            {...basicProps}
            width={120}
            title={i18n.t('issueAttributes.openedAt.label')}
            className="ProjectListMatrixTable__opened-at-column"
            dataGetter={this.dateDataGetter}
          />
        );
      case ISSUE_COLUMN_KEYS.CLOSED_AT:
        return (
          <Column
            {...basicProps}
            width={120}
            title={i18n.t('issueAttributes.closedAt.label')}
            className="ProjectListMatrixTable__closed-at-column"
            dataGetter={this.dateDataGetter}
          />
        );
      case ISSUE_COLUMN_KEYS.CREATED_AT:
        return (
          <Column
            {...basicProps}
            width={120}
            title={i18n.t('issueAttributes.createdAt.label')}
            className="ProjectListMatrixTable__created-at-column"
            dataGetter={this.dateDataGetter}
            sortField={ISSUE_SORTABLE_COLUMN_KEYS[columnKey]}
            sortable
          />
        );
      case ISSUE_COLUMN_KEYS.UPDATED_AT:
        return (
          <Column
            {...basicProps}
            width={120}
            title={i18n.t('issueAttributes.updatedAt.label')}
            className="ProjectListMatrixTable__updated-at-column"
            dataGetter={this.dateDataGetter}
            sortField={ISSUE_SORTABLE_COLUMN_KEYS[columnKey]}
            sortable
          />
        );
      case ISSUE_COLUMN_KEYS.UPDATED_BY:
        return (
          <Column
            {...basicProps}
            width={120}
            title={i18n.t('issueAttributes.updatedBy.label')}
            className="ProjectListMatrixTable__updated-by-column"
            dataGetter={this.userDisplayNameWithCompanyDataGetter}
            cellRenderer={this.renderUserCell}
            data-deleted-users-fetched={isDeletedUsersFetched}
          />
        );
      case ISSUE_COLUMN_KEYS.ISSUE_TYPE: {
        return (
          <Column
            {...basicProps}
            width={120}
            title={i18n.t('issueAttributes.issueType.label')}
            className="ProjectListMatrixTable__issue-type-column"
            sortField={ISSUE_SORTABLE_COLUMN_KEYS[columnKey]}
            sortable={this.props.isSortByTypeAndRootCauseEnabled}
            dataGetter={this.issueTypeDataGetter}
          />
        );
      }
      case ISSUE_COLUMN_KEYS.ISSUE_SUB_TYPE: {
        return (
          <Column
            {...basicProps}
            width={120}
            title={i18n.t('issueAttributes.issueSubType.label')}
            className="ProjectListMatrixTable__issue-sub-type-column"
            sortField={ISSUE_SORTABLE_COLUMN_KEYS[columnKey]}
            sortable={this.props.isSortByTypeAndRootCauseEnabled}
            dataGetter={this.issueTypeDataGetter}
          />
        );
      }
      case ISSUE_COLUMN_KEYS.DUE_DATE:
        return (
          <Column
            {...basicProps}
            width={120}
            title={i18n.t('issueAttributes.dueDate.label')}
            className={this.dueDateClassname}
            sortField={ISSUE_SORTABLE_COLUMN_KEYS[columnKey]}
            sortable
            dataGetter={this.duedateDataGetter}
          />
        );
      case ISSUE_COLUMN_KEYS.DOCUMENT:
        return (
          <Column
            {...basicProps}
            width={135}
            title={i18n.t('issueAttributes.document.label')}
            className="ProjectListMatrixTable__document-column"
            cellRenderer={this.renderDocument}
            fetchingIssuesUrns={this.props.fetchingIssuesUrns} // Little trick to force re-rendering of column
          />
        );
      case ISSUE_COLUMN_KEYS.ATTACHMENTS:
        return (
          <Column
            {...basicProps}
            width={60}
            headerRenderer={IconCell}
            cellRenderer={this.renderInnerCell}
            svgId="attachments-icon"
            className="ProjectListMatrixTable__attachments-column"
            title={i18n.t('issueAttributes.attachmentCount.label')}
            align={Column.Alignment.CENTER}
          />
        );
      case ISSUE_COLUMN_KEYS.COMMENTS:
        return (
          <Column
            {...basicProps}
            width={60}
            headerRenderer={IconCell}
            cellRenderer={this.renderInnerCell}
            svgId="comments-icon"
            className="ProjectListMatrixTable__comments-column"
            title={i18n.t('issueAttributes.commentCount.label')}
            align={Column.Alignment.CENTER}
          />
        );
      case ISSUE_COLUMN_KEYS.CHECKLIST:
        return (
          <Column
            {...basicProps}
            width={135}
            title={i18n.t('issueAttributes.checklist.label')}
            className="ProjectListMatrixTable__checklist-column"
            cellRenderer={this.renderChecklist}
            fetchingChecklistInstances={this.props.fetchingChecklistInstances} // Little trick to force re-rendering of column
          />
        );
      default:
        return null;
    }
  }

  renderCustomAttributeColumn(customAttributeId, hidden, frozen) {
    const customAttribute = this.props.customAttributes.find(ca => ca.id === customAttributeId);
    const dataGetter = ({rowData}) => this.getTextCellValue(getAttributeValueFromIssue(customAttribute, rowData));

    return (
      <Column
        key={customAttributeId}
        width={150}
        minWidth={60}
        hidden={hidden}
        frozen={frozen}
        resizable
        groupKey="CUSTOM_ATTRIBUTES"
        title={customAttribute.title}
        dataGetter={dataGetter}
      />
    );
  }

  renderColumns() {
    const columnsToRender = [];
    this.state.columns.forEach(({columnKey, hidden, frozen, isCustom}) => {
      const columnRenderer = isCustom ? this.renderCustomAttributeColumn : this.renderColumn;
      const renderedColumn = columnRenderer(columnKey, hidden, frozen);
      renderedColumn && columnsToRender.push(renderedColumn);
    });
    return columnsToRender;
  }

  renderEmptyState() {
    const {error} = this.props;
    if (!error) {
      return <EmptyStateNotFound />;
    }
    if (isTooManySublocationsError(error)) {
      return <EmptyStateSublocationsError />;
    }
    return <EmptyStateDataDidntLoad />;
  }

  render() {
    const {loading, tableData, currentSort, children, error} = this.props;
    const data = tableData.map(row => row.attributes);
    const sortBy = {
      order: currentSort.isAscending ? MatrixTable.SortOrder.ASC : MatrixTable.SortOrder.DESC,
      key: ISSUE_SORTABLE_KEYS_TO_COLUMN_KEYS[currentSort.sortField]
    };
    return (
      <MatrixTable
        fixed
        attachTestId
        showSettings
        autoGrow={data.length > 0}
        storageKey={this.storageKey}
        containerClassName="ProjectListMatrixTable"
        className="ProjectListMatrixTable__table"
        headerClassName="ProjectListMatrixTable__header"
        rowClassName="ProjectListMatrixTable__row"
        data={data}
        emptyRenderer={this.renderEmptyState}
        rowProps={this.getRowProps}
        onRowClick={this.onRowClick}
        onColumnSort={this.onColumnSort}
        loading={!error && loading}
        sortBy={sortBy}
        columnGroups={this.columnGroupKeys}
        footerRenderer={children}
        footerHeight={data.length > 0 ? 40 : 0}
      >
        {this.renderColumns()}
      </MatrixTable>
    );
  }
}

/* eslint-enable no-use-before-define */

// Defining column names for table use.
const ISSUE_COLUMN_KEYS = {
  STATUS: 'STATUS',
  ID: 'ID',
  TITLE: 'TITLE',
  LOCATION: 'LOCATION',
  CREATED_BY: 'CREATED_BY',
  ASSIGNED_TO: 'ASSIGNED_TO',
  COMPANY: 'COMPANY',
  DUE_DATE: 'DUE_DATE',
  DOCUMENT: 'DOCUMENT',
  ATTACHMENTS: 'ATTACHMENTS',
  COMMENTS: 'COMMENTS',
  LOCATION_ID: 'LOCATION_ID',
  ROOT_CAUSE: 'ROOT_CAUSE',
  ISSUE_TYPE: 'ISSUE_TYPE',
  ISSUE_SUB_TYPE: 'ISSUE_SUB_TYPE',
  OWNER: 'OWNER',
  OPENED_AT: 'OPENED_AT',
  CLOSED_AT: 'CLOSED_AT',
  CREATED_AT: 'CREATED_AT',
  UPDATED_AT: 'UPDATED_AT',
  UPDATED_BY: 'UPDATED_BY',
  CUSTOM_ATTRIBUTES: 'CUSTOM_ATTRIBUTES',
  CHECKLIST: 'CHECKLIST'
};

const ISSUE_COLUMN_DATA_KEYS = {
  [ISSUE_COLUMN_KEYS.STATUS]: ISSUE_ATTRIBUTE_NAMES.STATUS,
  [ISSUE_COLUMN_KEYS.ID]: ISSUE_ATTRIBUTE_NAMES.ID,
  [ISSUE_COLUMN_KEYS.TITLE]: ISSUE_ATTRIBUTE_NAMES.TITLE,
  [ISSUE_COLUMN_KEYS.LOCATION]: ISSUE_ATTRIBUTE_NAMES.LOCATION_DETAILS,
  [ISSUE_COLUMN_KEYS.CREATED_BY]: ISSUE_ATTRIBUTE_NAMES.CREATED_BY,
  [ISSUE_COLUMN_KEYS.CREATED_AT]: ISSUE_ATTRIBUTE_NAMES.CREATED_AT,
  [ISSUE_COLUMN_KEYS.OWNER]: ISSUE_ATTRIBUTE_NAMES.OWNER_ID,
  [ISSUE_COLUMN_KEYS.ASSIGNED_TO]: ISSUE_ATTRIBUTE_NAMES.ASSIGNED_TO,
  [ISSUE_COLUMN_KEYS.COMPANY]: ISSUE_ATTRIBUTE_NAMES.ASSIGNED_TO,
  [ISSUE_COLUMN_KEYS.DUE_DATE]: ISSUE_ATTRIBUTE_NAMES.DUE_DATE,
  [ISSUE_COLUMN_KEYS.DOCUMENT]: ISSUE_ATTRIBUTE_NAMES.LINKED_DOCUMENTS,
  [ISSUE_COLUMN_KEYS.CHECKLIST]: ISSUE_ATTRIBUTE_NAMES.LINKS,
  [ISSUE_COLUMN_KEYS.ATTACHMENTS]: ISSUE_ATTRIBUTE_NAMES.ATTACHMENT_COUNT,
  [ISSUE_COLUMN_KEYS.COMMENTS]: ISSUE_ATTRIBUTE_NAMES.COMMENT_COUNT,
  [ISSUE_COLUMN_KEYS.LOCATION_ID]: ISSUE_ATTRIBUTE_NAMES.LOCATION_ID,
  [ISSUE_COLUMN_KEYS.ROOT_CAUSE]: ISSUE_ATTRIBUTE_NAMES.ROOT_CAUSE_ID,
  [ISSUE_COLUMN_KEYS.ISSUE_TYPE]: ISSUE_ATTRIBUTE_NAMES.ISSUE_TYPE_ID,
  [ISSUE_COLUMN_KEYS.ISSUE_SUB_TYPE]: ISSUE_ATTRIBUTE_NAMES.ISSUE_SUB_TYPE_ID,
  [ISSUE_COLUMN_KEYS.OPENED_AT]: ISSUE_ATTRIBUTE_NAMES.OPENED_AT,
  [ISSUE_COLUMN_KEYS.CLOSED_AT]: ISSUE_ATTRIBUTE_NAMES.CLOSED_AT,
  [ISSUE_COLUMN_KEYS.UPDATED_AT]: ISSUE_ATTRIBUTE_NAMES.UPDATED_AT,
  [ISSUE_COLUMN_KEYS.UPDATED_BY]: ISSUE_ATTRIBUTE_NAMES.UPDATED_BY
};

const ISSUE_SORTABLE_COLUMN_KEYS = {
  [ISSUE_COLUMN_KEYS.STATUS]: ISSUE_ATTRIBUTE_NAMES.STATUS,
  [ISSUE_COLUMN_KEYS.ID]: ISSUE_ATTRIBUTE_NAMES.ID,
  [ISSUE_COLUMN_KEYS.TITLE]: ISSUE_ATTRIBUTE_NAMES.TITLE,
  [ISSUE_COLUMN_KEYS.LOCATION]: ISSUE_ATTRIBUTE_NAMES.LOCATION_DETAILS,
  [ISSUE_COLUMN_KEYS.DUE_DATE]: ISSUE_ATTRIBUTE_NAMES.DUE_DATE,
  [ISSUE_COLUMN_KEYS.ISSUE_TYPE]: ISSUE_ATTRIBUTE_NAMES.ISSUE_TYPE_ID,
  [ISSUE_COLUMN_KEYS.ISSUE_SUB_TYPE]: ISSUE_ATTRIBUTE_NAMES.ISSUE_SUB_TYPE_ID,
  [ISSUE_COLUMN_KEYS.OPENED_AT]: ISSUE_ATTRIBUTE_NAMES.OPENED_AT,
  [ISSUE_COLUMN_KEYS.CLOSED_AT]: ISSUE_ATTRIBUTE_NAMES.CLOSED_AT,
  [ISSUE_COLUMN_KEYS.CREATED_AT]: ISSUE_ATTRIBUTE_NAMES.CREATED_AT,
  [ISSUE_COLUMN_KEYS.UPDATED_AT]: ISSUE_ATTRIBUTE_NAMES.UPDATED_AT,
  [ISSUE_COLUMN_KEYS.ROOT_CAUSE]: ISSUE_ATTRIBUTE_NAMES.ROOT_CAUSE_ID
};

const ISSUE_SORTABLE_KEYS_TO_COLUMN_KEYS = {
  [ISSUE_SORTABLE_COLUMN_KEYS[ISSUE_COLUMN_KEYS.STATUS]]: ISSUE_COLUMN_KEYS.STATUS,
  [ISSUE_SORTABLE_COLUMN_KEYS[ISSUE_COLUMN_KEYS.ID]]: ISSUE_COLUMN_KEYS.ID,
  [ISSUE_SORTABLE_COLUMN_KEYS[ISSUE_COLUMN_KEYS.TITLE]]: ISSUE_COLUMN_KEYS.TITLE,
  [ISSUE_SORTABLE_COLUMN_KEYS[ISSUE_COLUMN_KEYS.LOCATION]]: ISSUE_COLUMN_KEYS.LOCATION,
  [ISSUE_SORTABLE_COLUMN_KEYS[ISSUE_COLUMN_KEYS.DUE_DATE]]: ISSUE_COLUMN_KEYS.DUE_DATE,
  [ISSUE_SORTABLE_COLUMN_KEYS[ISSUE_COLUMN_KEYS.ISSUE_TYPE]]: ISSUE_COLUMN_KEYS.ISSUE_TYPE,
  [ISSUE_SORTABLE_COLUMN_KEYS[ISSUE_COLUMN_KEYS.ISSUE_SUB_TYPE]]: ISSUE_COLUMN_KEYS.ISSUE_SUB_TYPE,
  [ISSUE_SORTABLE_COLUMN_KEYS[ISSUE_COLUMN_KEYS.OPENED_AT]]: ISSUE_COLUMN_KEYS.OPENED_AT,
  [ISSUE_SORTABLE_COLUMN_KEYS[ISSUE_COLUMN_KEYS.CLOSED_AT]]: ISSUE_COLUMN_KEYS.CLOSED_AT,
  [ISSUE_SORTABLE_COLUMN_KEYS[ISSUE_COLUMN_KEYS.CREATED_AT]]: ISSUE_COLUMN_KEYS.CREATED_AT,
  [ISSUE_SORTABLE_COLUMN_KEYS[ISSUE_COLUMN_KEYS.UPDATED_AT]]: ISSUE_COLUMN_KEYS.UPDATED_AT,
  [ISSUE_SORTABLE_COLUMN_KEYS[ISSUE_COLUMN_KEYS.ROOT_CAUSE]]: ISSUE_COLUMN_KEYS.ROOT_CAUSE
};

const hideInMinimalColumnMode = [
  ISSUE_COLUMN_KEYS.LOCATION,
  ISSUE_COLUMN_KEYS.CREATED_BY,
  ISSUE_COLUMN_KEYS.COMPANY,
  ISSUE_COLUMN_KEYS.DOCUMENT,
  ISSUE_COLUMN_KEYS.CHECKLIST,
  ISSUE_COLUMN_KEYS.LOCATION_ID,
  ISSUE_COLUMN_KEYS.ISSUE_TYPE,
  ISSUE_COLUMN_KEYS.ROOT_CAUSE
];

ProjectListMatrixTable.ColumnsOrder = [
  ISSUE_COLUMN_KEYS.STATUS,
  ISSUE_COLUMN_KEYS.ID,
  ISSUE_COLUMN_KEYS.ISSUE_TYPE,
  ISSUE_COLUMN_KEYS.ISSUE_SUB_TYPE,
  ISSUE_COLUMN_KEYS.TITLE,
  ISSUE_COLUMN_KEYS.LOCATION_ID,
  ISSUE_COLUMN_KEYS.LOCATION,
  ISSUE_COLUMN_KEYS.ASSIGNED_TO,
  ISSUE_COLUMN_KEYS.COMPANY,
  ISSUE_COLUMN_KEYS.DUE_DATE,
  ISSUE_COLUMN_KEYS.DOCUMENT,
  ISSUE_COLUMN_KEYS.OWNER,
  ISSUE_COLUMN_KEYS.ROOT_CAUSE,
  ISSUE_COLUMN_KEYS.OPENED_AT,
  ISSUE_COLUMN_KEYS.CLOSED_AT,
  ISSUE_COLUMN_KEYS.CREATED_BY,
  ISSUE_COLUMN_KEYS.CREATED_AT,
  ISSUE_COLUMN_KEYS.UPDATED_BY,
  ISSUE_COLUMN_KEYS.UPDATED_AT,
  ISSUE_COLUMN_KEYS.CUSTOM_ATTRIBUTES,
  ISSUE_COLUMN_KEYS.ATTACHMENTS,
  ISSUE_COLUMN_KEYS.COMMENTS,
  ISSUE_COLUMN_KEYS.CHECKLIST
];

ProjectListMatrixTable.HiddenColumns = [
  ISSUE_COLUMN_KEYS.LOCATION,
  ISSUE_COLUMN_KEYS.OWNER,
  ISSUE_COLUMN_KEYS.ROOT_CAUSE,
  ISSUE_COLUMN_KEYS.OPENED_AT,
  ISSUE_COLUMN_KEYS.CLOSED_AT,
  ISSUE_COLUMN_KEYS.CREATED_BY,
  ISSUE_COLUMN_KEYS.CREATED_AT,
  ISSUE_COLUMN_KEYS.UPDATED_BY,
  ISSUE_COLUMN_KEYS.UPDATED_AT,
  ISSUE_COLUMN_KEYS.CHECKLIST
];

ProjectListMatrixTable.FrozenColumns = {
  [ISSUE_COLUMN_KEYS.STATUS]: Column.FrozenDirection.LEFT,
  [ISSUE_COLUMN_KEYS.ID]: Column.FrozenDirection.LEFT
};

ProjectListMatrixTable.displayName = 'ProjectListMatrixTable';

ProjectListMatrixTable.defaultProps = {
  locationsTreeNodes: [],
  projectId: ''
};

ProjectListMatrixTable.propTypes = {
  tableData: issueWrapperArrayType.isRequired,
  currentSort: sortPropType.isRequired,
  onSort: PropTypes.func.isRequired,
  onRowClick: PropTypes.func.isRequired,
  documentCollection: documentCollectionType,
  navigateToIssueInDocumentAction: PropTypes.func.isRequired,
  isLocationsTreeLoading: PropTypes.bool,
  isLocationsLoadingError: PropTypes.bool,
  locationsTreeNodes: locationsTreeNodesType,
  rootCauseEntitiesMap: PropTypes.object,
  fetchingIssuesUrns: PropTypes.array,
  issueTypesAndSubTypesTitleMap: idToTitleMapType.isRequired,
  rowLinkEnabled: PropTypes.bool.isRequired,
  isMinimalTableColumns: PropTypes.bool,
  currentUser: oxygenUserType,
  oxygen: oxygenType.isRequired,
  customAttributes: PropTypes.arrayOf(PropTypes.object),
  children: PropTypes.node,
  loading: PropTypes.bool,
  error: PropTypes.object,
  projectId: PropTypes.string,
  analyticsId: PropTypes.string,
  isSortByTypeAndRootCauseEnabled: PropTypes.bool,
  getUserDisplayNameWithCompany: PropTypes.func.isRequired,
  getEntityDisplayNameById: PropTypes.func.isRequired,
  isDeletedUsersFetched: PropTypes.bool.isRequired,
  checklistCollection: PropTypes.object.isRequired,
  fetchingChecklistInstances: PropTypes.array
};

export default ProjectListMatrixTable;
